import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useHealthStore, type HealthStatus } from '@/stores/healthStore'
import { useUIStore } from '@/stores/uiStore'

const BASE_URL = import.meta.env.VITE_API_URL || ''
const ONLINE_INTERVAL_MS = 15_000
const OFFLINE_INTERVAL_MS = 5_000
const PING_TIMEOUT_MS = 8_000

async function pingHealth(signal: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/api/health`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      signal,
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Polls /api/health to detect backend availability. Faster polling while
 * offline so recovery is detected quickly. Pauses polling when the tab is
 * hidden to avoid wasted wake-ups, and immediately re-pings on visibility
 * return.
 *
 * Side-effects on transition:
 *   offline → online: fire success toast, invalidate stale queries
 *   online  → offline: handled by the banner reading from the store
 */
export function useBackendHealth(): {
  status: HealthStatus
  lastSuccess: number | null
} {
  const status = useHealthStore((s) => s.status)
  const lastSuccess = useHealthStore((s) => s.lastSuccess)
  const recordSuccess = useHealthStore((s) => s.recordSuccess)
  const recordFailure = useHealthStore((s) => s.recordFailure)

  const queryClient = useQueryClient()
  const addToast = useUIStore((s) => s.addToast)
  const prevStatusRef = useRef<HealthStatus>(status)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null

    async function tick() {
      if (cancelled) return
      controller = new AbortController()
      const timeoutId = setTimeout(() => controller?.abort(), PING_TIMEOUT_MS)
      const ok = await pingHealth(controller.signal)
      clearTimeout(timeoutId)
      if (cancelled) return
      if (ok) recordSuccess()
      else recordFailure()
      const next = useHealthStore.getState().status
      const delay = next === 'offline' ? OFFLINE_INTERVAL_MS : ONLINE_INTERVAL_MS
      timer = setTimeout(tick, delay)
    }

    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        if (timer) clearTimeout(timer)
        tick()
      } else {
        if (timer) clearTimeout(timer)
        timer = null
        controller?.abort()
      }
    }

    tick()
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      controller?.abort()
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [recordSuccess, recordFailure])

  // Fire side-effects on offline → online transition
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if (prev === 'offline' && status === 'online') {
      addToast({
        type: 'success',
        title: 'Back online',
        message: 'Reconnected to the server. Refreshing your data…',
        duration: 5000,
      })
      queryClient.invalidateQueries()
    }
  }, [status, addToast, queryClient])

  return { status, lastSuccess }
}
